import * as React from "react";
import { connect } from "react-redux";
import { SubPages } from "../../../../../store/AppDisplays";
import { PartnerFilters } from "../../../../../store/Partners/Types";
import { remoteClearError, remoteClearResponse, remoteTrigger } from "../../../../../store/RemoteActions";
import { RemoteScope } from "../../../../../store/RemoteTypes";
import { DispatchFunc } from "../../../../../store/ActionTypes";
import { clearFilters, updateFilters } from "../../../../../store/FilterActions";
import {
    FilterScope,
    getFilters,
    hasSearchableFilters,
} from "../../../../../store/FilterTypes";
import { ReduxState } from "../../../../../store/ReduxState";
import { ThemePalette } from "@vwpfs/mary-air-lib/theme/_Types";
import { hasValueValidation } from "@vwpfs/mary-air-lib/components/air/07-organisms/Form/Field.utils";
import { Alignment, ButtonsWrapper, Orientation } from "@vwpfs/mary-air-lib/components/air/06-molecules/ButtonsWrapper";
import { Div } from "@vwpfs/mary-air-lib/components/air/03-base/Div";
import { Grid } from "@vwpfs/mary-air-lib/components/air/03-base/Grid";
import { Button } from "@vwpfs/mary-air-lib/components/air/05-atoms/Button";
import { Form } from "@vwpfs/mary-air-lib/components/air/07-organisms/Form";
import { FieldTypes, FormField } from "@vwpfs/mary-air-lib/components/air/07-organisms/Form/Field";

/**
 *
 */
type setFilterFn = (filters: Partial<PartnerFilters>) => void;
type clearFiltersFn = (filters?: Array<keyof PartnerFilters>) => void;

/**
 *
 */
interface OwnProps {
    loadingResults: boolean;
    defaultFilters?: PartnerFilters;
    collapseFilter?: boolean;
    currentPage?: SubPages;
}

/**
 *
 */
interface StateProps {
    searchEnabled: boolean;
    filters: PartnerFilters;
}

/**
 *
 */
interface DispatchProps {
    setFilter: setFilterFn;
    clearFilters: clearFiltersFn;
    loadResults: () => void;
    clearResults: () => void;
}

/**
 *
 */
type Props = OwnProps & StateProps & DispatchProps;

/**
 *
 */
export const mapStateToProps = (s: ReduxState, p: OwnProps): StateProps => ({
    searchEnabled: hasSearchableFilters(s, FilterScope.PARTNERS, p.defaultFilters),
    filters: getFilters(s, FilterScope.PARTNERS),
});

/**
 *
 */
export const mapDispatchToProps = (dispatch: DispatchFunc): DispatchProps => ({
    setFilter: (filters) => dispatch(updateFilters(filters, FilterScope.PARTNERS)),
    clearFilters: (filters) => {
        dispatch(clearFilters(filters, FilterScope.PARTNERS));
    },
    loadResults: () => dispatch(remoteTrigger(RemoteScope.PARTNER_RESULTS_CI, { skip: 0 })),
    clearResults: () => (
        dispatch(remoteClearResponse(RemoteScope.PARTNER_RESULTS_CI)),
        dispatch(remoteClearError(RemoteScope.PARTNER_RESULTS_CI))
    ),
});

/**
 *
 * @param props
 */
export class FilterComp
    extends React.Component<Props, {}> {

    public constructor(props: Props) {
        super(props);
        this.partnerNumberValidation = this.partnerNumberValidation.bind(this);
        this.onChangePartnerNumber = this.onChangePartnerNumber.bind(this);
        this.nameValidation = this.nameValidation.bind(this);
        this.onChangeName = this.onChangeName.bind(this);
        this.ibanValidation = this.ibanValidation.bind(this);
        this.onChangeIBAN = this.onChangeIBAN.bind(this);
        this.initFilters = this.initFilters.bind(this);
    }

    public componentDidMount() {
        this.initFilters();
    }

    public componentWillUnmount() {
        this.props.clearResults();
    }

    public render() {
        return (
            <Form onSubmit={this.onSearchFilter}>
                <Div className="scl-b-row">
                    {this.props.currentPage !== SubPages.POLICY_CREATE &&
                        <Grid
                            size={{ xs: 12, md: 6 }}
                        >
                            <FormField
                                type={FieldTypes.Text}
                                label="Name"
                                onChange={this.onChangeName}
                                value={this.props.filters?.name ?? ""}
                                validationFunction={this.nameValidation}
                            />
                        </Grid>
                    }
                    <Grid
                        size={{ xs: 12, md: 6 }}
                    >
                        <FormField
                            type={FieldTypes.Text}
                            label="IBAN"
                            onChange={this.onChangeIBAN}
                            value={this.props.filters?.iban ?? ""}
                            validationFunction={this.ibanValidation}
                        />
                    </Grid>
                </Div>
                <Div className="scl-b-row">
                    <Grid
                        size={{ xs: 12, md: 6 }}
                    >
                        <FormField
                            type={FieldTypes.Text}
                            label="Partner number"
                            onChange={this.onChangePartnerNumber}
                            value={this.props.filters?.partnerNumber ?? ""}
                            validationFunction={this.partnerNumberValidation}
                        />
                    </Grid>
                </Div>
                <ButtonsWrapper alignment={Alignment.CENTER} defaultOrientation={Orientation.HORIZONTAL}>
                    <Button
                        className="scl-a-btn--big scl-a-btn scl-a-btn--brand-link"
                        link={{ onClick: this.initFilters }}
                        disabled={!this.props.searchEnabled || this.props.loadingResults}
                    >
                        Clear filters
                    </Button>
                    {" "}
                    <Button
                        theme={{ palette: ThemePalette.BRAND_ACCENT }}
                        className="scl-a-btn--big"
                        buttonType="submit"
                        disabled={this.props.loadingResults}
                    >
                        Search
                    </Button>
                </ButtonsWrapper>
            </Form>
        );
    }

    private partnerNumberValidation(value: string | number) {
        return hasValueValidation(value.toString(),
            "Partner number provided");
    }

    private onChangePartnerNumber(value?: string | number) {
        return this.onChange("partnerNumber", value ? value.toString() : "");
    }

    private nameValidation(value: string | number) {
        return hasValueValidation(
            value.toString(),
            "Name provided",
        );
    }

    private onChangeName(value?: string | number) {
        return this.onChange("name", value?.toString() ?? "");
    }

    private ibanValidation(value: string | number) {
        return hasValueValidation(
            value.toString(),
            "IBAN provided",
        );
    }

    private onChangeIBAN(value?: string | number) {
        return this.onChange("iban", value?.toString() ?? "");
    }

    private initFilters() {
        this.clearFilters();
        if (this.props.defaultFilters) {
            this.props.setFilter(this.props.defaultFilters);
        }
        this.props.loadResults();
    }

    private clearFilters = () => {
        const filters =
            (Object.keys(this.props.filters) as Array<keyof PartnerFilters>);

        this.props.clearFilters(filters);
    };

    private onChange = <K extends keyof PartnerFilters>(key: K, value: PartnerFilters[K]) => {
        this.props.setFilter({
            [key]: value,
        });
    };

    private onSearchFilter = () => {
        this.props.loadResults();
    };
}

/**
 *
 */
export const Filter = connect(
    mapStateToProps,
    mapDispatchToProps,
)(FilterComp);
